import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Eye, Package, PackagePlus, Upload, RefreshCcwIcon, XCircle, PlusCircle, Pencil, Archive, ArchiveRestore } from "lucide-react";
import AddProductModal from "../../../components/Forms/AddProductModal";
import RestockModal from "../../../components/Forms/RestockProductModal";
import ManagerLayout from "../Layout";
import AdminLayout from "../../Admin/Layout";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useProducts } from "../../../hooks/useProducts";
import { useStores } from "../../../hooks/useStores";
import { extractUniqueCategories } from "../../../lib/extractUniqueCategories";
import type { Product, StoreItem } from "../../../types/store";
import { archiveProducts, fetchProducts, unarchiveProducts } from "../../../slices/productSlice";
import { uploadToCloudinary } from "../../../lib/uploadToCloudinary";
import api from "../../../lib/axios";
import BulkImportWizard from "../../../components/BulkImportWizard";
import { useSubscriptionGuard } from "../../../hooks/useSubscriptionGuard";
import EntityLimitCard from "../../../components/EntityLimitCard";
import SubscriptionGuardModal from "../../../components/SubscriptionGuardModal";

const ManagerStoreItemsPage : React.FC = () => {

    const activeRole = useAppSelector((state) => state.auth.activeRole);
    const Layout = activeRole === "Admin" ? AdminLayout : ManagerLayout;

    // MODAL VISIBILITY STATES
    const [showAddModal, setShowAddModal] = useState(false);
    const [showBulkImportModal, setShowBulkImportModal] = useState(false);
    const [showRestockModal, setShowRestockModal] = useState(false);
    const [showSubscriptionGuardModal, setShowSubscriptionGuardModal] = useState<boolean>(false);
    
    // FILTER STATES
    const [categoryFilter, setCategoryFilter] = useState("All");
    const [storeFilter, setStoreFilter] = useState("All");
    const [searchTerm, setSearchTerm] = useState("");
    
    
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [imageUploading, setImageUploading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editValues, setEditValues] = useState({ name: "", category: "", cost_price: "", selling_price: "" });
    const [isSaving, setIsSaving] = useState(false);

    // Archive view state
    const [viewMode, setViewMode] = useState<"active" | "archived">("active");
    const [archivedProducts, setArchivedProducts] = useState<Product[]>([]);
    const [archivedLoading, setArchivedLoading] = useState(false);

    // Reset edit state whenever a different product is selected
    useEffect(() => {
      if (selectedProduct) {
        setEditValues({
          name: selectedProduct.name,
          category: selectedProduct.category,
          cost_price: String(selectedProduct.cost_price),
          selling_price: String(selectedProduct.selling_price),
        });
        setEditMode(false);
      }
    }, [selectedProduct?.id]);

    const handleEditSave = async () => {
      if (!selectedProduct) return;
      setIsSaving(true);
      try {
        await api.put(`/product/update/${selectedProduct.id}`, {
          name: editValues.name,
          category: editValues.category,
          cost_price: parseFloat(editValues.cost_price),
          selling_price: parseFloat(editValues.selling_price),
          store_id: selectedProduct.store_id,
        });
        dispatch(fetchProducts());
        setEditMode(false);
      } catch {
        // user can retry
      } finally {
        setIsSaving(false);
      }
    };

    const handleImageUpdate = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !selectedProduct) return;
      setImageUploading(true);
      try {
        const imageUrl = await uploadToCloudinary(file);
        await api.put(`/product/update/${selectedProduct.id}`, {
          image_url: imageUrl,
          store_id: selectedProduct.store_id,
        });
        dispatch(fetchProducts());
      } catch {
        // silently fail — user can retry
      } finally {
        setImageUploading(false);
        // reset so the same file can be re-selected if needed
        e.target.value = "";
      }
    };
    const storeAssignments = useAppSelector((state) => state.auth.user?.StoreAssignment);

    // console.log("Store Assignments: ", storeAssignments);

    const dispatch = useAppDispatch();
    const { products, loading } = useProducts();
    const { stores } = useStores();
    const { usageOverview, permissions, isMaxProductCountReached } = useSubscriptionGuard();
    const [searchParams, setSearchParams] = useSearchParams();

    // Auto-open restock modal from URL deep link (e.g. ?productId=xxx&action=restock)
    useEffect(() => {
      const productId = searchParams.get("productId");
      const action = searchParams.get("action");
      if (productId && action === "restock" && !loading && products.length > 0) {
        const product = products.find((p) => p.id === productId);
        if (product) {
          setSelectedProduct(product);
          setShowRestockModal(true);
        }
        setSearchParams({}, { replace: true });
      }
    }, [searchParams, loading, products, setSearchParams]);

    // Sync selectedProduct when products list is updated (e.g., after restock)
    useEffect(() => {
      if (selectedProduct) {
        const updatedProduct = products.find((p) => p.id === selectedProduct.id);
        if (updatedProduct) {
          setSelectedProduct(updatedProduct);
        }
      }
    }, [products]);

    // Fetch archived products when switching to archived view
    useEffect(() => {
      if (viewMode !== "archived") return;
      setArchivedLoading(true);
      api.get("/product/all", { params: { archived: "true" } })
        .then((res) => setArchivedProducts(res.data.data))
        .catch(() => setArchivedProducts([]))
        .finally(() => setArchivedLoading(false));
    }, [viewMode]);

    const handleSwitchTab = (tab: "active" | "archived") => {
      setViewMode(tab);
      setSelectedProduct(null);
    };

    const handleArchive = async (p: Product) => {
      await dispatch(archiveProducts([p.id]));
      const { toast } = await import("react-toastify");
      toast.success(`"${p.name}" archived`);
    };

    const handleUnarchive = async (p: Product) => {
      await dispatch(unarchiveProducts([p.id]));
      setArchivedProducts((prev) => prev.filter((ap) => ap.id !== p.id));
      dispatch(fetchProducts());
      const { toast } = await import("react-toastify");
      toast.success(`"${p.name}" unarchived`);
      handleSwitchTab("active");
    };

    const categories = extractUniqueCategories(products);
    const assignedStoreIds = new Set(
     storeAssignments?.map((a:any) => a.Store.id)
    );

    const matchedStores = stores.filter((store: any) => 
      assignedStoreIds.has(store.id)
    );

    // Filtering logic
    const filteredProducts = useMemo(() => {
      return products.filter(
        (p) =>
          (categoryFilter === "All" || p.category === categoryFilter) &&
          (storeFilter === "All" || p.store_id === storeFilter) &&
          p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [products, categoryFilter, storeFilter, searchTerm]);

    const filteredArchived = useMemo(() => {
      return archivedProducts.filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [archivedProducts, searchTerm]);


  return (
    <Layout>
      <div className="p-2">
        {/* Header and Controls */}
        {/* <h2 className="text-2xl text-left font-bold text-gray-800 mb-3">Store Inventory</h2> */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">

            {/* On larger screens: everything on one line */}
            <div className="hidden sm:flex items-end justify-between gap-3">
                <input
                    type="text"
                    placeholder="Search by product name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input px-3 py-2 w-56"
                />
                <div className="flex flex-col items-start">
                    <span className="form-label text-xs">Filter by Category</span>
                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="form-input px-3 py-2 w-40"
                    >
                      <option key={"all"} value="All">All</option>
                    {categories.map((c) => (
                        <option key={c} value={c}>
                        {c}
                        </option>
                    ))}
                    </select>
                </div>
                <div className="flex flex-col">
                    <span className="form-label text-xs">Filter by Store</span>
                    <select
                        value={storeFilter}
                        onChange={(e) => setStoreFilter(e.target.value)}
                        className="form-input px-3 py-2 w-40"
                    >
                      <option key={"all"} value="All">All Stores</option>
                      {matchedStores.map((s) => (
                          <option key={s.id} value={s.id}>
                          {s.name}
                          </option>
                      ))}
                    </select>
                </div>


                  {/* CURRENT PRODUCT COUNT */}
                <EntityLimitCard 
                      limit={permissions?.itemLimit as number}
                      limitReached={isMaxProductCountReached() as boolean}
                      value={usageOverview?.productCount as number}
                      entity="products"
                  />

                {/* BUTTONS */}
                <button
                    onClick={() => {
                        dispatch(fetchProducts())
                    }}
                    className="ms-6 px-2 py-2 rounded-md bg-transparent text-gray-800 border border-gray-200"
                    >
                        <RefreshCcwIcon />
                </button>

                <button
                  onClick={() => {
                    if(isMaxProductCountReached()) {
                      setShowSubscriptionGuardModal(true);
                    } else {
                      setShowAddModal(true)
                    }
                  }}
                  className="px-3 py-2 rounded-md bg-primary text-white flex items-center gap-2 sm:text-xs md:text-md"
                >
                <PlusCircle /> Add Product
                </button>

                <button
                  onClick={() => {
                    if(isMaxProductCountReached()) {
                      setShowSubscriptionGuardModal(true);
                    } else {
                      setShowBulkImportModal(true)
                    }
                  }}
                  className="px-3 py-2 rounded-md bg-blue-700 hover:bg-blue-800 text-white flex items-center gap-2 sm:text-xs md:text-md"
                >
                  <Upload /> Bulk Import
                </button>
            </div>

            {/* Mobile layout: 2 stacked rows (search/filter + buttons) */}
            <div className="sm:hidden flex flex-col gap-3">
                {/* Row 1: Search */}
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input px-3 py-2 w-full"
                />
                {/* Row 2: filters */}
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col items-start">
                        <span className="form-label text-xs">Filter by Category</span>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="form-input px-3 py-2 w-full"
                        >
                          <option key={"all"} value="All">All</option>
                          {categories.map((c) => (
                              <option key={c} value={c}>
                              {c}
                              </option>
                          ))}
                        </select>
                    </div>
                    <div className="flex flex-col">
                        <span className="form-label text-xs">Filter by Store</span>
                        <select
                            value={storeFilter}
                            onChange={(e) => setStoreFilter(e.target.value)}
                            className="form-input px-3 py-2 w-full"
                        >
                          <option key={"all"} value="All">All Stores</option>
                          {matchedStores.map((s) => (
                              <option key={s.id} value={s.id}>
                              {s.name}
                              </option>
                          ))}
                        </select>
                    </div>
                </div>

                {/* Row 3: buttons side by side */}
                <div className="flex justify-end gap-2">
                  <button
                      onClick={() => {
                          dispatch(fetchProducts())
                      }}
                      className="flex items-center justify-center px-2 py-2 rounded-md bg-transparent text-gray-800 border border-gray-200 w-1/5"
                      >
                        <RefreshCcwIcon />
                  </button>
                  <button
                      onClick={() => setShowAddModal(true)}
                      className="form-btn-mini flex items-center justify-center w-2/5"
                  >
                      <Plus size={18} /> 
                      Add
                  </button>
                  <button
                      onClick={() => setShowBulkImportModal(true)}
                      className="form-btn-mini bg-blue-700 hover:bg-blue-800 text-white flex items-center justify-center w-2/5"
                  >
                      <Upload size={18} /> Import
                  </button>
                </div>
            </div>
        </div>

        {/* Active / Archived tab toggle */}
        <div className="flex gap-1 mb-3">
          <button
            onClick={() => handleSwitchTab("active")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors ${viewMode === "active" ? "bg-primary text-white border-primary" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
          >
            Active
          </button>
          <button
            onClick={() => handleSwitchTab("archived")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors flex items-center gap-1.5 ${viewMode === "archived" ? "bg-primary text-white border-primary" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
          >
            <Archive size={14} /> Archived
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Table Section */}
          <div className="lg:w-2/3 bg-white rounded-lg shadow-md overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-blue-100 text-gray-700">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Cost</th>
                  <th className="px-4 py-2">Price</th>
                  <th className="px-4 py-2">Quantity</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {(viewMode === "active" ? loading : archivedLoading) ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="animate-pulse border-b">
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-3/4"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-1/2"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-1/3"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-1/3"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-1/4"></div></td>
                      <td className="px-4 py-3 text-right"><div className="h-4 bg-gray-200 rounded w-1/2 ml-auto"></div></td>
                    </tr>
                  ))
                ) : viewMode === "active" ? (
                  filteredProducts.length ? (
                    filteredProducts.map((p) => (
                      <tr key={p.id} className="border-b hover:bg-gray-50 cursor-pointer">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                            ) : (
                              <Package size={20} className="text-gray-400 flex-shrink-0" />
                            )}
                            {p.name}
                          </div>
                        </td>
                        <td className="px-4 py-3">{p.category}</td>
                        <td className="px-4 py-3">₵{p.cost_price}</td>
                        <td className="px-4 py-3">₵{p.selling_price}</td>
                        <td className="px-4 py-3 font-semibold">{p.total_quantity || 0}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setSelectedProduct(p)}
                              className="text-[#090263] flex items-center gap-1"
                            >
                              <Eye size={16} /> View
                            </button>
                            <button
                              onClick={() => {
                                setSelectedProduct(p);
                                setShowRestockModal(true);
                              }}
                              className="text-green-600 flex items-center gap-1"
                            >
                              <PackagePlus size={16} /> Restock
                            </button>
                            <button
                              onClick={() => handleArchive(p)}
                              className="text-amber-600 flex items-center gap-1"
                            >
                              <Archive size={16} /> Archive
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-16 text-center">
                        {products.length === 0 ? (
                          <div className="flex flex-col items-center gap-3 text-gray-500">
                            <PackagePlus size={40} className="text-gray-300" />
                            <p className="font-medium text-gray-600">No products yet</p>
                            <p className="text-sm">Start by adding your first product to the inventory.</p>
                            <button
                              onClick={() => {
                                if (isMaxProductCountReached()) {
                                  setShowSubscriptionGuardModal(true);
                                } else {
                                  setShowAddModal(true);
                                }
                              }}
                              className="mt-2 px-4 py-2 rounded-md bg-primary text-white flex items-center gap-2 text-sm"
                            >
                              <PlusCircle size={16} /> Add Product
                            </button>
                          </div>
                        ) : (
                          <p className="text-gray-500 italic">No products match your filters.</p>
                        )}
                      </td>
                    </tr>
                  )
                ) : (
                  filteredArchived.length ? (
                    filteredArchived.map((p) => (
                      <tr key={p.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                            ) : (
                              <Package size={20} className="text-gray-400 flex-shrink-0" />
                            )}
                            {p.name}
                          </div>
                        </td>
                        <td className="px-4 py-3">{p.category}</td>
                        <td className="px-4 py-3">₵{p.cost_price}</td>
                        <td className="px-4 py-3">₵{p.selling_price}</td>
                        <td className="px-4 py-3 font-semibold">{p.total_quantity || 0}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleUnarchive(p)}
                            className="text-blue-600 flex items-center gap-1 ml-auto"
                          >
                            <ArchiveRestore size={16} /> Unarchive
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-16 text-center">
                        <div className="flex flex-col items-center gap-3 text-gray-500">
                          <Archive size={40} className="text-gray-300" />
                          <p className="font-medium text-gray-600">No archived products</p>
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* Details Panel (Desktop) */}
          <div className="hidden lg:block text-left lg:w-1/3 bg-white rounded-lg shadow-md p-4">
            {selectedProduct ? (
              <>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-gray-800 truncate pr-2">
                    {selectedProduct.name}
                  </h3>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setEditMode((m) => !m)}
                      className="text-gray-500 hover:text-primary"
                      title={editMode ? "Cancel edit" : "Edit details"}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setSelectedProduct(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <XCircle color="red" size={20} />
                    </button>
                  </div>
                </div>

                {/* Product image */}
                <div className="mb-4">
                  {selectedProduct.image_url ? (
                    <img
                      src={selectedProduct.image_url}
                      alt={selectedProduct.name}
                      className="w-full h-40 object-cover rounded-md"
                    />
                  ) : (
                    <div className="w-full h-40 bg-gray-100 rounded-md flex items-center justify-center text-gray-400 text-xs">
                      No image
                    </div>
                  )}
                  <label
                    htmlFor="product-image-upload"
                    className={`mt-2 flex items-center justify-center gap-1.5 w-full py-1.5 rounded-md border border-gray-300 text-xs text-gray-600 cursor-pointer hover:bg-gray-50 ${imageUploading ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    {imageUploading ? "Uploading…" : selectedProduct.image_url ? "Change Image" : "Upload Image"}
                  </label>
                  <input
                    id="product-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpdate}
                  />
                </div>

                {editMode ? (
                  <div className="space-y-2 mb-4">
                    <div>
                      <label className="form-label text-xs">Name</label>
                      <input
                        className="form-input w-full"
                        value={editValues.name}
                        onChange={(e) => setEditValues((v) => ({ ...v, name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="form-label text-xs">Category</label>
                      <input
                        className="form-input w-full"
                        value={editValues.category}
                        onChange={(e) => setEditValues((v) => ({ ...v, category: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="form-label text-xs">Cost Price (₵)</label>
                        <input
                          type="number"
                          className="form-input w-full"
                          value={editValues.cost_price}
                          onChange={(e) => setEditValues((v) => ({ ...v, cost_price: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="form-label text-xs">Selling Price (₵)</label>
                        <input
                          type="number"
                          className="form-input w-full"
                          value={editValues.selling_price}
                          onChange={(e) => setEditValues((v) => ({ ...v, selling_price: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={handleEditSave}
                        disabled={isSaving}
                        className="form-btn-mini flex-1"
                      >
                        {isSaving ? "Saving…" : "Save Changes"}
                      </button>
                      <button
                        onClick={() => setEditMode(false)}
                        className="flex-1 px-4 py-2 rounded-md border text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 mb-1">
                      Category:{" "}
                      <span className="font-medium">{selectedProduct.category}</span>
                    </p>
                    <p className="text-sm text-gray-500 mb-1">
                      Cost Price:{" "}
                      <span className="font-medium">₵{selectedProduct.cost_price}</span>
                    </p>
                    <p className="text-sm text-gray-500 mb-1">
                      Selling Price:{" "}
                      <span className="font-medium">₵{selectedProduct.selling_price}</span>
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Total Quantity:{" "}
                      <span className="font-medium">{selectedProduct.total_quantity}</span>
                    </p>
                  </>
                )}

                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Batches
                </h4>
               <div className="max-h-64 overflow-y-auto divide-y divide-gray-200">
                  {selectedProduct?.StoreItem?.length > 0 ? (
                    selectedProduct.StoreItem.map((b: StoreItem) => (
                      <div
                        key={b.id}
                        className="flex justify-between items-center py-3 px-2 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-800">
                            Qty: {b.quantity} pcs
                          </span>
                          <span className="text-xs text-gray-500">
                            MFG:{" "}
                            {b.mfg_date
                              ? new Date(b.mfg_date).toLocaleDateString()
                              : "N/A"}
                          </span>
                          <span className="text-xs text-gray-500">
                            EXP:{" "}
                            {b.exp_date
                              ? new Date(b.exp_date).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400">
                          Batch ID: <span className="font-mono">{b.id?.slice(0, 8)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-sm text-gray-500 py-4">
                      No batch details available
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 italic">
                Select a product to view details
              </div>
            )}
          </div>
        </div>

        {/* Product Details Modal (Mobile) */}
        {selectedProduct && (
          <div className="lg:hidden fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white text-left w-full max-w-md rounded-lg p-4 shadow-lg relative">
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-3 right-3 text-gray-600 hover:text-gray-800"
              >
                <XCircle color="red" size={20} />
              </button>
              <div className="flex items-center gap-2 mb-2 pr-8">
                <h3 className="text-lg font-bold truncate">{selectedProduct.name}</h3>
                <button
                  onClick={() => setEditMode((m) => !m)}
                  className="text-gray-500 hover:text-primary flex-shrink-0"
                  title={editMode ? "Cancel edit" : "Edit details"}
                >
                  <Pencil size={16} />
                </button>
              </div>

              {/* Product image (mobile) */}
              <div className="mb-3">
                {selectedProduct.image_url ? (
                  <img
                    src={selectedProduct.image_url}
                    alt={selectedProduct.name}
                    className="w-full h-36 object-cover rounded-md"
                  />
                ) : (
                  <div className="w-full h-36 bg-gray-100 rounded-md flex items-center justify-center text-gray-400 text-xs">
                    No image
                  </div>
                )}
                <label
                  htmlFor="product-image-upload-mobile"
                  className={`mt-2 flex items-center justify-center gap-1.5 w-full py-1.5 rounded-md border border-gray-300 text-xs text-gray-600 cursor-pointer hover:bg-gray-50 ${imageUploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  {imageUploading ? "Uploading…" : selectedProduct.image_url ? "Change Image" : "Upload Image"}
                </label>
                <input
                  id="product-image-upload-mobile"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpdate}
                />
              </div>

              {editMode ? (
                <div className="space-y-2 mb-3">
                  <div>
                    <label className="form-label text-xs">Name</label>
                    <input
                      className="form-input w-full"
                      value={editValues.name}
                      onChange={(e) => setEditValues((v) => ({ ...v, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="form-label text-xs">Category</label>
                    <input
                      className="form-input w-full"
                      value={editValues.category}
                      onChange={(e) => setEditValues((v) => ({ ...v, category: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="form-label text-xs">Cost Price (₵)</label>
                      <input
                        type="number"
                        className="form-input w-full"
                        value={editValues.cost_price}
                        onChange={(e) => setEditValues((v) => ({ ...v, cost_price: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="form-label text-xs">Selling Price (₵)</label>
                      <input
                        type="number"
                        className="form-input w-full"
                        value={editValues.selling_price}
                        onChange={(e) => setEditValues((v) => ({ ...v, selling_price: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleEditSave}
                      disabled={isSaving}
                      className="form-btn-mini flex-1"
                    >
                      {isSaving ? "Saving…" : "Save Changes"}
                    </button>
                    <button
                      onClick={() => setEditMode(false)}
                      className="flex-1 px-4 py-2 rounded-md border text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm mb-1">Category: {selectedProduct.category}</p>
                  <p className="text-sm mb-1">Cost Price: ₵{selectedProduct.cost_price}</p>
                  <p className="text-sm mb-1">Selling Price: ₵{selectedProduct.selling_price}</p>
                  <p className="text-sm mb-3">Total Quantity: {selectedProduct.total_quantity}</p>
                </>
              )}

              <h4 className="text-sm font-semibold mb-2">Batches</h4>
              <div className="max-h-48 overflow-y-auto text-sm">
                {(selectedProduct.StoreItem) && (selectedProduct.StoreItem.length > 0) &&  selectedProduct.StoreItem.map((b: any) => (
                  <div
                    key={b.id}
                    className="flex justify-between border-b py-1"
                  >
                    <span>{b.id}</span>
                    <span>{b.quantity} pcs</span>
                  </div>
                ))}

                {!selectedProduct.StoreItem && (
                  <div className="text-gray-600">
                    No batch details
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modals */}

        {/* ADD PRODUCT */}
        {showAddModal && (
          <AddProductModal 
            onClose={() => setShowAddModal(false)} 
            categories={categories}  
          />
        )}

        {/* RESTOCK PRODUCT */}
        {showRestockModal && selectedProduct && (
          <RestockModal
            product={selectedProduct}
            onClose={() => setShowRestockModal(false)}
          />
        )}

        {/* BULK IMPORT PRODUCTS */}
        {showBulkImportModal && (
          <BulkImportWizard
            stores={matchedStores}
            onClose={() => {
              setShowBulkImportModal(false);
              dispatch(fetchProducts());
            }}
          />
        )}

         {/* SUBSCRIPTION GUARD */}
          <SubscriptionGuardModal 
              isOpen={showSubscriptionGuardModal}
              onClose={() => setShowSubscriptionGuardModal(false)}
              type="products"
          />

      </div>
    </Layout>
  );
}

export default ManagerStoreItemsPage;